import { useState } from 'react';

import { useFeatureFlag } from '../featureFlags/useFeatureFlag';
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEvent';

type UseMediaQueryOptions = {
  defaultValue?: boolean;
  initializeWithValue?: boolean;
};

const IS_SERVER = typeof window === 'undefined';

export function useMediaQuery(
  query: string,
  { defaultValue = false, initializeWithValue = true }: UseMediaQueryOptions = {},
): boolean {
  const getMatches = (query: string): boolean => {
    if (IS_SERVER) {
      return defaultValue;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState<boolean>(() => {
    if (initializeWithValue) {
      return getMatches(query);
    }
    return defaultValue;
  });

  // Handles the change event of the media query.
  function handleChange() {
    setMatches(getMatches(query));
  }

  useIsomorphicLayoutEffect(() => {
    const matchMedia = window.matchMedia(query);

    // Triggered at the first client-side load and if query changes
    handleChange();

    // Use deprecated `addListener` and `removeListener` to support Safari < 14 (#135)
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener('change', handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener('change', handleChange);
      }
    };
  }, [query]);

  return matches;
}

export function useMobileDevice(): boolean {
  return useMediaQuery('(max-width: 768px), (pointer: coarse) and (hover: none)');
}

/**
 * The field layout of a record (the job record's phone and tablet screens): up to tablet width,
 * or any touch-only screen. Desktop above 1024px with a mouse keeps the desktop layout.
 *
 * Behind the `mobile.field-layout` flag: off keeps the desktop layout on every screen, as before
 * October 2026. Outside prod the flag is always on.
 */
export function useFieldLayout(): boolean {
  const matches = useMediaQuery('(max-width: 1024px), (pointer: coarse) and (hover: none)');
  // Applies on phones and tablets only, and to the whole screen: the dev legend lists it there,
  // not on desktop, and frames the viewport rather than a region.
  const released = useFeatureFlag('mobile.field-layout', { applies: matches, scope: 'page' });
  return released && matches;
}

export function useTabletOrMobileDevice(): boolean {
  return useMediaQuery('(max-width: 1024px)');
}

export function useTouchDevice(): boolean {
  // Width cap: some desktop environments (RDP, thin clients) report a coarse
  // no-hover pointer, and they must never get the phone layout.
  return useMediaQuery('(max-width: 1024px) and (pointer: coarse) and (hover: none)');
}
